import { useEffect } from 'react';
import useResolver from './useResolver.js';
import useSuspenseResource from './useSuspenseResource.js';
/** Loads through Suspense or an Effect, using the same loader in either mode. */
export default function useResource(request, suspense, onError) {
    const [state, dispatch] = useResolver();
    const value = useSuspenseResource(suspense ? request : undefined, onError);
    const load = request === null || request === void 0 ? void 0 : request.load;
    // Callers memoize the loader with its inputs. Callback changes alone should
    // not restart a load, and Suspense uses the cache's input comparison instead.
    useEffect(() => {
        dispatch({ type: 'RESET' });
        if (suspense || !load) {
            return;
        }
        let active = true;
        let task;
        const loadResource = async () => {
            if (!active) {
                return;
            }
            try {
                task = load();
                const nextValue = await task.promise;
                if (active) {
                    dispatch({ type: 'RESOLVE', value: nextValue });
                }
            }
            catch (reason) {
                if (active) {
                    const error = reason instanceof Error ? reason : new Error(String(reason));
                    dispatch({ type: 'REJECT', error });
                }
            }
        };
        // Strict Mode can disconnect the Effect before a task needs to be started.
        void Promise.resolve().then(loadResource);
        return () => {
            active = false;
            void Promise.resolve()
                .then(() => { var _a; return (_a = task === null || task === void 0 ? void 0 : task.dispose) === null || _a === void 0 ? void 0 : _a.call(task); })
                .catch(() => { });
            // A disposed document must not survive a mode change or Activity reveal.
            dispatch({ type: 'RESET' });
        };
    }, [dispatch, load, suspense]);
    return suspense ? { value, error: undefined } : state;
}
